/**
 * DropdownMenu — Base UI Menu primitive.
 *
 * Design system mapping:
 *   • Popup: bg-white dark:bg-webex-gray-950
 *            border webex-gray-300 dark:border-webex-gray-700
 *            rounded-lg shadow-overlay py-1 min-w-[10rem]
 *   • Item:  px-3 py-1.5 text-sm gap-2
 *            highlight: bg webex-gray-100 dark: webex-gray-850
 *   • Separator: 1px webex-gray-200 dark: webex-gray-800
 *   • Group label: px-3 pt-2 pb-0.5 text-[10px] uppercase tracking-wide opacity-60
 *
 * Compound:
 *   <DropdownMenu.Root>
 *     <DropdownMenu.Trigger render={<Button>Open</Button>} />
 *     <DropdownMenu.Content>
 *       <DropdownMenu.Item onClick={...}>Item</DropdownMenu.Item>
 *       <DropdownMenu.Separator />
 *       <DropdownMenu.Sub>
 *         <DropdownMenu.SubTrigger>More</DropdownMenu.SubTrigger>
 *         <DropdownMenu.SubContent>...</DropdownMenu.SubContent>
 *       </DropdownMenu.Sub>
 *     </DropdownMenu.Content>
 *   </DropdownMenu.Root>
 */
import * as React from "react"
import { Menu as BaseMenu } from "@base-ui-components/react/menu"
import { cn } from "@lib/cn"

const Root = BaseMenu.Root
const Trigger = BaseMenu.Trigger
const Group = BaseMenu.Group
const RadioGroup = BaseMenu.RadioGroup
const SubmenuRoot = BaseMenu.SubmenuRoot

const itemBase = [
  "flex cursor-pointer items-center gap-2 px-3 py-1.5 text-sm",
  "text-webex-gray-1000 dark:text-white",
  "data-[highlighted]:bg-webex-gray-100 dark:data-[highlighted]:bg-webex-gray-850",
  "data-[disabled]:opacity-50 data-[disabled]:cursor-not-allowed",
  "outline-none select-none",
].join(" ")

interface ContentProps extends React.ComponentPropsWithoutRef<typeof BaseMenu.Popup> {
  side?: "top" | "right" | "bottom" | "left"
  align?: "start" | "center" | "end"
  sideOffset?: number
}

const Content = React.forwardRef<HTMLDivElement, ContentProps>(
  ({ className, children, side = "bottom", align = "start", sideOffset = 4, ...props }, ref) => (
    <BaseMenu.Portal>
      <BaseMenu.Positioner side={side} align={align} sideOffset={sideOffset} className="z-50">
        <BaseMenu.Popup
          ref={ref}
          className={cn(
            "min-w-[10rem] rounded-md border bg-white dark:bg-webex-gray-950",
            "border-webex-gray-300 dark:border-webex-gray-700",
            "shadow-[0_4px_12px_rgba(0,0,0,0.12)] dark:shadow-[0_4px_12px_rgba(0,0,0,0.6)]",
            "py-1 outline-none",
            "data-[starting-style]:opacity-0 data-[ending-style]:opacity-0",
            "transition-opacity duration-150 motion-reduce:transition-none",
            className,
          )}
          {...props}
        >
          {children}
        </BaseMenu.Popup>
      </BaseMenu.Positioner>
    </BaseMenu.Portal>
  ),
)
Content.displayName = "DropdownMenu.Content"

const Item = React.forwardRef<
  HTMLDivElement,
  React.ComponentPropsWithoutRef<typeof BaseMenu.Item>
>(({ className, ...props }, ref) => (
  <BaseMenu.Item ref={ref} className={cn(itemBase, className)} {...props} />
))
Item.displayName = "DropdownMenu.Item"

const CheckboxItem = React.forwardRef<
  HTMLDivElement,
  React.ComponentPropsWithoutRef<typeof BaseMenu.CheckboxItem>
>(({ className, children, ...props }, ref) => (
  <BaseMenu.CheckboxItem
    ref={ref}
    className={cn(itemBase, "pr-8 relative", className)}
    {...props}
  >
    <BaseMenu.CheckboxItemIndicator className="absolute right-2 inline-flex">
      <svg viewBox="0 0 16 16" className="size-3.5" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
        <polyline points="3.5,8.5 6.5,11.5 12.5,5" />
      </svg>
    </BaseMenu.CheckboxItemIndicator>
    {children}
  </BaseMenu.CheckboxItem>
))
CheckboxItem.displayName = "DropdownMenu.CheckboxItem"

const RadioItem = React.forwardRef<
  HTMLDivElement,
  React.ComponentPropsWithoutRef<typeof BaseMenu.RadioItem>
>(({ className, children, ...props }, ref) => (
  <BaseMenu.RadioItem
    ref={ref}
    className={cn(itemBase, "pr-8 relative", className)}
    {...props}
  >
    <BaseMenu.RadioItemIndicator className="absolute right-2 inline-flex">
      <span className="size-1.5 rounded-full bg-current" aria-hidden />
    </BaseMenu.RadioItemIndicator>
    {children}
  </BaseMenu.RadioItem>
))
RadioItem.displayName = "DropdownMenu.RadioItem"

function Separator({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      role="separator"
      className={cn("my-1 h-px bg-webex-gray-200 dark:bg-webex-gray-800", className)}
      {...props}
    />
  )
}

const GroupLabel = React.forwardRef<
  HTMLDivElement,
  React.ComponentPropsWithoutRef<typeof BaseMenu.GroupLabel>
>(({ className, ...props }, ref) => (
  <BaseMenu.GroupLabel
    ref={ref}
    className={cn(
      "px-3 pt-2 pb-0.5 text-[10px] font-semibold uppercase tracking-wider",
      "text-webex-gray-600 dark:text-webex-gray-400",
      className,
    )}
    {...props}
  />
))
GroupLabel.displayName = "DropdownMenu.GroupLabel"

const SubTrigger = React.forwardRef<
  HTMLDivElement,
  React.ComponentPropsWithoutRef<typeof BaseMenu.SubmenuTrigger>
>(({ className, children, ...props }, ref) => (
  <BaseMenu.SubmenuTrigger
    ref={ref}
    className={cn(itemBase, "pr-2", className)}
    {...props}
  >
    {children}
    <span className="ml-auto text-webex-gray-600 dark:text-webex-gray-400" aria-hidden>
      <svg viewBox="0 0 12 12" className="size-3" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="4.5,3 8,6 4.5,9" />
      </svg>
    </span>
  </BaseMenu.SubmenuTrigger>
))
SubTrigger.displayName = "DropdownMenu.SubTrigger"

const SubContent = Content

export const DropdownMenu = {
  Root,
  Trigger,
  Content,
  Item,
  CheckboxItem,
  RadioItem,
  RadioGroup,
  Separator,
  Group,
  GroupLabel,
  Sub: SubmenuRoot,
  SubTrigger,
  SubContent,
}
